import React, { Fragment, useState } from "react";
import {
  makeStyles,
  Button,
  Input,
  Text,
  Link,
  Avatar,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogActions,
  DialogTitle,
  DialogTrigger,
} from "@fluentui/react-components";
import { EditFilled, ErrorCircle16Filled, CheckmarkCircle16Filled } from "@fluentui/react-icons";
import { getAccountData, logOut, updateUsername, deleteAccount } from "../taskpane";

/**
 * Interface définissant les propriétés du composant ProfilPage.
 */
interface ProfilProps {
  handleMenuReload: () => void; // Fonction pour recharger le menu principal
  setCurrentView: (view: string) => void; // Fonction pour changer la vue actuelle
}

/**
 * Composant ProfilPage : représente la page de profil de l'utilisateur.
 * Permet de modifier le nom d'utilisateur, de consulter les informations du compte,
 * de changer le mot de passe, de supprimer le compte ou de se déconnecter.
 */
const ProfilPage: React.FC<ProfilProps> = ({ setCurrentView, handleMenuReload }) => {
  const styles = useStyles();

  // États locaux
  const [email, setEmail] = useState(""); // Adresse e-mail de l'utilisateur
  const [username, setUsername] = useState(""); // Nom d'utilisateur actuel
  const [newUsername, setNewUsername] = useState(""); // Nouveau nom d'utilisateur
  const [requests_left, setRequestsLeft] = useState(""); // Nombre de requêtes restantes
  const [settingUpUsername, setSettingUpUsername] = useState(false); // État pour modifier le nom d'utilisateur
  const [loading, setLoading] = useState(false); // État de chargement
  const [success, setSuccess] = useState(false); // État de succès
  const [errors, setErrors] = useState(""); // Message d'erreur
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false); // État pour ouvrir/fermer la boîte de dialogue de suppression

  /**
   * Fonction pour changer le nom d'utilisateur.
   * Valide l'entrée et met à jour le nom d'utilisateur via l'API.
   */
  async function changeUsername() {
    setLoading(true);
    setErrors("");
    setSuccess(false);

    const trimmedUsername = newUsername.trim();
    const usernameRegex = /^[A-Za-z]+$/;

    if (trimmedUsername.length < 2 || trimmedUsername.length > 16) {
      setErrors("Le nom d'utilisateur doit contenir entre 2 et 16 caractères.");
      setLoading(false);
      return;
    }

    if (!usernameRegex.test(trimmedUsername)) {
      setErrors("Le nom d'utilisateur ne peut contenir que des lettres (A-Z, a-z).");
      setLoading(false);
      return;
    }

    try {
      await updateUsername(newUsername);
      setUsername(newUsername);
      setSuccess(true);
      setSettingUpUsername(false);
      handleMenuReload();
    } catch (error) {
      setErrors(error as string);
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Récupère les informations du compte utilisateur.
   */
  async function getGreeting() {
    await getAccountData()
      .then((user) => {
        setEmail(user.email);
        if (username == "") {
          setUsername(user.username);
          setNewUsername(user.username);
        }
        setRequestsLeft(user.requests_left.toString());
      })
      .catch((error) => {
        console.error(error);
      });
  }
  getGreeting();

  // Rendu du composant
  return (
    <div className={styles.container} style={{ alignItems: "center" }} data-testid="profil-page">
      {/* Avatar de l'utilisateur */}
      <Avatar color="colorful" size={40} name={username} />

      {/* Modification du nom d'utilisateur */}
      <div id="nameContentDiv" className={styles.formField} style={{ textAlign: "center" }}>
        <ul
          style={{
            display: "flex",
            flexDirection: "row",
            listStyleType: "none",
            padding: 0,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <li>
            <Text style={{ fontSize: 30 }}>{username}</Text>
          </li>

          <li style={{ marginRight: "10px" }}>
            <Popover open={settingUpUsername} trapFocus>
              <PopoverTrigger disableButtonEnhancement>
                <Button
                  onClick={() => setSettingUpUsername((prev) => !prev)}
                  style={{ backgroundColor: "#1AA05D", alignItems: "center", marginLeft: "10px" }}
                  icon={<EditFilled />}
                ></Button>
              </PopoverTrigger>

              <PopoverSurface>
                <div>
                  <h3 className={styles.contentHeader}>Changer nom d'utilisateur</h3>
                  <Input
                    placeholder="Nouveau nom d'utilisateur"
                    value={newUsername}
                    onChange={(e) => {
                      const input = e.target.value;
                      if (/^[A-Za-z]*$/.test(input) && input.length <= 16) {
                        setNewUsername(input);
                      }
                    }}
                    maxLength={16}
                  />
                  <Button disabled={newUsername == ""} onClick={changeUsername}>
                    Changer
                  </Button>
                </div>
              </PopoverSurface>
            </Popover>
          </li>
        </ul>
        <div className={styles.statusMessageContainer}>
          {errors && (
            <div className={styles.errorText}>
              <ErrorCircle16Filled style={{ marginRight: "8px" }} />
              <Text>{errors}</Text>
            </div>
          )}

          {success && (
            <div className={styles.successText}>
              <CheckmarkCircle16Filled style={{ marginRight: "8px" }} />
              <Text>Nom d'utilisateur modifié avec succès!</Text>
            </div>
          )}
        </div>
      </div>

      {/* Informations utilisateur */}
      <div style={{ textAlign: "center" }}>
        <Text>
          Courriel: <b>{email}</b>
        </Text>
      </div>
      <br />
      <div style={{ textAlign: "center" }}>
        <Text>
          Nombre de requêtes restantes: <b>{requests_left}</b>
        </Text>
      </div>
      <Link onClick={() => setCurrentView("CreditPage")}>Rechargez vos crédits!</Link>
      <br />

      {/* Actions utilisateur */}
      <div className={styles.buttonContainer}>
        <Button onClick={() => setCurrentView("ChangerDeMotDePasse")}>
          Changer votre mot de passe
        </Button>
      </div>
      <br />
      <div className={styles.buttonContainer}>
        <Dialog
          open={isDeleteDialogOpen}
          onOpenChange={(_, data) => setIsDeleteDialogOpen(data.open)}
        >
          <DialogTrigger disableButtonEnhancement>
            <Button
              style={{ backgroundColor: "#8b0000", color: "white" }}
              onClick={() => setIsDeleteDialogOpen(true)}
            >
              Supprimer mon compte
            </Button>
          </DialogTrigger>

          <DialogSurface>
            <DialogBody>
              <DialogTitle>Confirmer la suppression</DialogTitle>
              <Text style={{ gridArea: "2/1/2/4" }}>
                Êtes-vous sûr de vouloir supprimer votre compte? Cette action est irréversible.
                <br />
                <div className={styles.errorText} style={{ fontSize: "11px", marginTop: "5px" }}>
                  <ErrorCircle16Filled style={{ marginRight: "8px" }} />
                  Attention : Vous ne pourrez pas réutiliser ce courriel pendant 30 jours après la
                  suppression.
                </div>
              </Text>
              <DialogActions>
                <Button onClick={() => setIsDeleteDialogOpen(false)}>Annuler</Button>
                <Button
                  style={{ backgroundColor: "#8b0000", color: "white" }}
                  onClick={() => {
                    deleteAccount();
                    localStorage.clear();
                    window.location.reload();
                  }}
                >
                  Supprimer
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      </div>
      <br />
      <div className={styles.buttonContainer}>
        <Button
          style={{ backgroundColor: "#8b0000", color: "white" }}
          onClick={() => {
            logOut();
            localStorage.clear();
            window.location.reload();
          }}
        >
          Déconnexion
        </Button>
      </div>
    </div>
  );
};

export default ProfilPage;

const useStyles = makeStyles({
  contentHeader: {
    marginTop: "0",
  },
  container: {
    marginTop: "10px",
    display: "flex",
    width: "100%",
    flexDirection: "column",
    justifyContent: "center",
    position: "relative",
  },
  formField: {
    marginBottom: "12px",
    width: "100%",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
  heading: {
    margin: "0px 0px 16px 0px",
    width: "100%",
  },
  statusMessageContainer: {
    display: "flex",
    justifyContent: "center",
  },
  errorText: {
    color: "red",
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
  },
  successText: {
    color: "green",
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
  },
  spinnerContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "20px",
  },
});
